import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';


const Avatar = ({ name, avatar, size = 36 }) => (
  <div style={{
    width: `${size}px`, height: `${size}px`, borderRadius: '50%', flexShrink: 0,
    backgroundColor: '#f97316', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: `${size * 0.38}px`, fontWeight: '700',
    color: 'white', overflow: 'hidden',
  }}>
    {avatar
      ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      : name?.charAt(0)?.toUpperCase() || '?'
    }
  </div>
);

export default function ChatBell({ onOpenChat }) {
  const { token, user } = useAuth();
  const [chats, setChats] = useState([]);
  const [open, setOpen] = useState(false);
  const [unreadChats, setUnreadChats] = useState([]);
  const ref = useRef();

  useEffect(() => {
    if (token) fetchChats();
    const interval = setInterval(() => { if (token) fetchChats(); }, 15000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchChats = async () => {
    try {
      const data = await apiFetch('/packages/mis-chats', {}, token);
      const ocultos = JSON.parse(localStorage.getItem(`chatsOcultos_${user?.id}`) || '[]');
      const visibles = data.filter(pkg => !ocultos.includes(pkg.id));

      // Ordenar por fecha del último mensaje (más reciente primero) — el backend
      // los devuelve ordenados por fecha de creación del envío, no de actividad,
      // así que un chat con un mensaje nuevo puede quedarse "enterrado" abajo si no
      // reordenamos aquí.
      const ordenados = [...visibles].sort((a, b) => {
        const fechaA = a.Message?.[0]?.createdAt || a.createdAt;
        const fechaB = b.Message?.[0]?.createdAt || b.createdAt;
        return new Date(fechaB) - new Date(fechaA);
      });

      setChats(ordenados);
      const visitas = JSON.parse(localStorage.getItem('chatVisitas') || '{}');
      const noLeidos = ordenados.filter(pkg => {
        if (!pkg.Message || pkg.Message.length === 0) return false;
        const ultimoMensaje = new Date(pkg.Message[0].createdAt);
        const ultimaVisita = visitas[pkg.id] ? new Date(visitas[pkg.id]) : new Date(0);
        return ultimoMensaje > ultimaVisita && pkg.Message[0].userId !== user?.id;
      });
      setUnreadChats(noLeidos);
    } catch {}
  };

  const handleHideChat = (e, pkgId) => {
    e.stopPropagation();
    const key = `chatsOcultos_${user?.id}`;
    const ocultos = JSON.parse(localStorage.getItem(key) || '[]');
    localStorage.setItem(key, JSON.stringify([...ocultos, pkgId]));
    setChats(prev => prev.filter(c => c.id !== pkgId));
    setUnreadChats(prev => prev.filter(c => c.id !== pkgId));
  };
const handleOpenChat = (pkg) => {
    const visitas = JSON.parse(localStorage.getItem('chatVisitas') || '{}');
    visitas[pkg.id] = new Date().toISOString();
    localStorage.setItem('chatVisitas', JSON.stringify(visitas));
    setUnreadChats(prev => prev.filter(c => c.id !== pkg.id));
    onOpenChat(pkg.id, true);
    setOpen(false);
  };

  
  // Mostrar todos los participantes menos el usuario actual
  const getParticipants = (pkg) => {
    const seen = new Set([Number(user?.id)]);
    const parts = [];
    [pkg.sender, pkg.driver, pkg.receiver].forEach(p => {
      if (p && p.id && !seen.has(Number(p.id))) {
        seen.add(Number(p.id));
        parts.push(p);
      }
    });
    return parts;
  };
  const getLastMessage = (pkg) => {
    if (!pkg.Message || pkg.Message.length === 0) return null;
    return pkg.Message[0];
  };

  const isUnread = (pkg) => unreadChats.some(c => c.id === pkg.id);

  const formatTime = (date) => {
    const d = new Date(date);
    const hoy = new Date();
    if (d.toDateString() === hoy.toDateString()) {
      return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: 'relative', backgroundColor: open ? '#1a1d24' : 'transparent',
          color: open ? '#f97316' : '#6b7280', padding: '8px', borderRadius: '10px',
          border: open ? '1px solid #2a2d35' : '1px solid transparent',
          display: 'flex', cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        <FaComments style={{ fontSize: '16px' }} />
        {unreadChats.length > 0 && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px',
            backgroundColor: '#f97316', color: 'white', fontSize: '10px',
            fontWeight: '700', width: '18px', height: '18px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #111318',
          }}>
            {unreadChats.length > 9 ? '9+' : unreadChats.length}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '44px',
          backgroundColor: '#1a1d24', border: '1px solid #2a2d35',
          borderRadius: '16px', width: '300px', zIndex: 50,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 16px', borderBottom: '1px solid #2a2d35',
            backgroundColor: '#1e2128',
          }}>
            <div>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '14px' }}>Chats</p>
              {unreadChats.length > 0 && (
                <p style={{ color: '#f97316', fontSize: '10px', fontWeight: '600' }}>
                  {unreadChats.length} sin leer
                </p>
              )}
            </div>
            <button onClick={() => setOpen(false)} style={{
              backgroundColor: '#111318', color: '#6b7280', padding: '6px',
              borderRadius: '8px', border: '1px solid #2a2d35', cursor: 'pointer',
              display: 'flex',
            }}>
              <FaTimes style={{ fontSize: '11px' }} />
            </button>
          </div>

          {/* Lista */}
          <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
            {chats.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ fontSize: '32px', opacity: 0.15, marginBottom: '8px' }}>💬</div>
                <p style={{ color: '#6b7280', fontSize: '13px' }}>No tienes chats activos</p>
              </div>
            ) : (
              chats.map(pkg => {
                const participants = getParticipants(pkg);
                const lastMsg = getLastMessage(pkg);
                const unread = isUnread(pkg);
                return (
                  <div key={pkg.id} onClick={() => handleOpenChat(pkg)} style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #2a2d35',
                    cursor: 'pointer',
                    backgroundColor: unread ? '#f9731608' : 'transparent',
                    transition: 'background-color 0.15s',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    {/* Contenido */}
                    <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'center', minWidth: 0 }}>
                      {/* Avatares apilados */}
                      <div style={{ position: 'relative', width: '40px', height: '36px', flexShrink: 0 }}>
                        {participants.slice(0, 2).map((p, i) => (
                          <div key={p.id} style={{ position: 'absolute', left: `${i * 10}px`, top: `${i * 4}px`, zIndex: participants.length - i }}>
                            <Avatar name={p.name} avatar={p.avatar} size={28} />
                          </div>
                        ))}
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                          <p style={{
                            color: 'white', fontSize: '13px',
                            fontWeight: unread ? '700' : '600',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: '160px',
                          }}>
                            {participants.map(p => p.name).join(', ') || 'Chat'}
                          </p>
                          {lastMsg && (
                            <p style={{ color: '#3a3d45', fontSize: '10px', flexShrink: 0 }}>
                              {formatTime(lastMsg.createdAt)}
                            </p>
                          )}
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '11px', marginBottom: '2px' }}>
                          {pkg.from} → {pkg.to}
                        </p>
                        {lastMsg && (
                          <p style={{
                            color: unread ? '#f97316' : '#4b5563',
                            fontSize: '12px', fontWeight: unread ? '600' : '400',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {lastMsg.text}
                          </p>
                        )}
                      </div>
                      {/* Punto no leído */}
                      {unread && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f97316', flexShrink: 0 }} />
                      )}
                    </div>

                    {/* Botón ocultar */}
                    <button
                      onClick={(e) => handleHideChat(e, pkg.id)}
                      style={{
                        backgroundColor: 'transparent', color: '#3a3d45',
                        padding: '6px', border: 'none', borderRadius: '6px',
                        cursor: 'pointer', flexShrink: 0, display: 'flex',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                      onMouseLeave={e => e.currentTarget.style.color = '#3a3d45'}
                      title="Ocultar chat"
                    >
                      <FaTimes style={{ fontSize: '11px' }} />
                    </button>
                  </div>
                );
             }))}
          </div>
        </div>
      )}
    </div>
  );
}